// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { z } from 'zod';

/** Shared email validation. Rejects '+' (sub-addressing) in the local part. */
const email = z
  .string()
  .min(1, 'Email is required.')
  .email('Please enter a valid email address.')
  .max(255, 'Email must be at most 255 characters.')
  .refine(
    (val) => {
      const atIndex = val.indexOf('@');
      if (atIndex === -1) return true; // .email() will catch this
      return !val.slice(0, atIndex).includes('+');
    },
    { message: 'Email addresses with "+" are not allowed.' },
  );

/** Shared password validation. */
const password = z
  .string()
  .min(8, 'Password must be at least 8 characters.')
  .max(128, 'Password must be at most 128 characters.');

/** Login form schema. */
export const loginSchema = z.object({
  email,
  password: z.string().min(1, 'Password is required.'),
});

/** Registration form schema with password confirmation. */
export const registerSchema = z
  .object({
    email,
    password,
    confirmPassword: z.string().min(1, 'Please confirm your password.'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  });

/** Forgot password form schema. */
export const forgotPasswordSchema = z.object({
  email,
});

/** Reset password form schema with password confirmation. */
export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, 'Reset token is required.'),
    newPassword: password,
    confirmPassword: z.string().min(1, 'Please confirm your password.'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  });

/** Login confirmation code form schema (the emailed 6-digit code). */
export const loginCodeSchema = z.object({
  code: z.string().regex(/^[0-9]{6}$/, 'Enter the 6-digit code from your email.'),
});

export type LoginCodeFormData = z.infer<typeof loginCodeSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
